import React, { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../store/authStore';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import useEstMobile from '../hooks/useEstMobile';
import FormulaireDelegation from '../components/FormulaireDelegation';
import { IconeHorloge, IconePlume, IconeRefuse } from '../components/IconesSVG';

const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const jour = (d) => new Date(d).toISOString().slice(0, 10);

// Trois etats distincts, et non un simple booleen : une delegation revoquee
// avant terme ne se lit pas comme une delegation arrivee a echeance.
const etatDe = (d) => {
  const aujourdhui = jour(Date.now());
  if (!d.actif) return d.date_fin < aujourdhui ? 'Expirée' : 'Désactivée';
  if (d.date_fin < aujourdhui) return 'Expirée';
  if (d.date_debut > aujourdhui) return 'À venir';
  return 'Active';
};

const COULEURS = {
  Active: { color: '#1E8449', background: '#EAFAF1' },
  'À venir': { color: '#1A5E8A', background: '#EAF4FB' },
  Expirée: { color: '#9A7D0A', background: '#FEF9E7' },
  Désactivée: { color: '#96281B', background: '#FDEDEC' },
};

export default function DelegationsPage() {
  const estMobile = useEstMobile();
  const { user } = useAuthStore();
  const { allerDashboard } = useDashboard();
  const [delegations, setDelegations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const roleUser = user?.roles?.[0]?.role || user?.role;
  const filialeId = user?.filiale_id || user?.roles?.[0]?.filiale?._id;

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const res = await api.get('/delegations');
      setDelegations(res.data || []);
      setErreur('');
    } catch (e) {
      setErreur(e.response?.data?.message || 'Chargement impossible');
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const revoquer = async (id) => {
    if (!window.confirm('Révoquer cette délégation ?')) return;
    try {
      await api.delete(`/delegations/${id}`);
      charger();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Révocation impossible');
    }
  };

  return (
    <div style={s.page}>
      <div data-entete style={s.entete}>
        <button type="button" onClick={allerDashboard} style={s.retour}>← Tableau de bord</button>
        <div style={s.titrePage}><IconePlume taille={16} /> Mes délégations de signature</div>
      </div>

      <div style={{ ...s.corps, gridTemplateColumns: estMobile ? '1fr' : '1fr 360px' }}>
        <div>
          <div style={s.sousTitre}>Historique</div>
          {erreur && <div style={s.erreur}>{erreur}</div>}

          {chargement ? (
            <div style={s.vide}><IconeHorloge taille={16} /> Chargement…</div>
          ) : delegations.length === 0 ? (
            <div style={s.vide}>Aucune délégation. Utilisez le formulaire pour en créer une.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {delegations.map((d) => {
                const etat = etatDe(d);
                return (
                  <div key={d.id} style={s.ligne} className="ligne-tactile">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.nom}>
                        {d.delegue?.prenom} {d.delegue?.nom}
                        <span style={s.identifiant}> ({d.delegue?.identifiant})</span>
                      </div>
                      <div style={s.detail}>
                        Du {dateFr(d.date_debut)} au {dateFr(d.date_fin)}
                        {d.montant_max
                          ? ` · plafond ${Number(d.montant_max).toLocaleString('fr-FR')} FCFA`
                          : ' · sans plafond'}
                      </div>
                    </div>
                    <span style={{ ...s.etat, ...COULEURS[etat] }}>{etat}</span>
                    {etat === 'Active' || etat === 'À venir' ? (
                      <button type="button" onClick={() => revoquer(d.id)} style={s.revoquer}>
                        <IconeRefuse taille={13} /> Révoquer
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <FormulaireDelegation roleUser={roleUser} filialeId={filialeId} onCree={charger} />
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F0F2F5', fontFamily: 'Inter, system-ui, sans-serif' },
  entete: { background: '#0D1B2A', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16 },
  retour: { background: 'transparent', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#8899AA', padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', minHeight: 36 },
  titrePage: { color: '#fff', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
  corps: { maxWidth: 1100, margin: '0 auto', padding: '24px 20px', display: 'grid', gap: 20, alignItems: 'start' },
  sousTitre: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  ligne: { background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  nom: { fontSize: 14, fontWeight: 700, color: '#0D1B2A' },
  identifiant: { fontSize: 12, fontWeight: 400, color: '#aaa' },
  detail: { fontSize: 12, color: '#666', marginTop: 3 },
  etat: { fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, flexShrink: 0 },
  revoquer: { background: 'transparent', border: '1px solid #C0392B', color: '#C0392B', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', minHeight: 36, display: 'flex', alignItems: 'center', gap: 5 },
  vide: { background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 12, padding: 24, textAlign: 'center', color: '#888', fontSize: 13 },
  erreur: { background: '#FDEDEC', border: '1px solid #C0392B', color: '#96281B', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 12 },
};
